export type RecoverableSpace = {
  space_id?: string;
  name?: string;
};

/**
 * Client gave up or never got a response. The space may already exist server-side.
 * HTTP 4xx/5xx with a response body are not treated as this case (409 is handled separately).
 */
export function isTimeoutOrNetworkError(error: unknown): boolean {
  const err = error as {
    code?: string;
    message?: string;
    response?: unknown;
  } | null;
  if (!err) {
    return false;
  }
  const code = err.code ?? "";
  const message = err.message ?? "";
  if (
    code === "ECONNABORTED" ||
    code === "ERR_NETWORK" ||
    code === "ETIMEDOUT" ||
    code === "ECONNRESET"
  ) {
    return true;
  }
  if (/timeout|network error|network request failed/i.test(message)) {
    return true;
  }
  return false;
}

/**
 * Prefer the space the user just named, then the first space (same as the 409 fallback).
 */
export function pickSpaceToAdvance<T extends RecoverableSpace>(
  spaces: T[] | undefined | null,
  requestedName?: string,
): T | undefined {
  if (!spaces?.length) {
    return undefined;
  }
  const wanted = requestedName?.trim();
  if (wanted) {
    const match = spaces.find((space) => space.name === wanted);
    if (match?.space_id) {
      return match;
    }
  }
  const first = spaces[0];
  return first?.space_id ? first : undefined;
}
